'use client';

import type { ElementType } from 'react';

import React, { forwardRef } from 'react';

import { cn } from '@eliadmin/core/src/utils/style-utils';
import { normalizeListSpacers } from '@/utils/list-html';

interface ParagraphProps {
  children?: React.ReactNode;
  innerHtml?: string;
  className?: string;
  size?: 'xs' | 'sm' | 'base' | 'lg' | 'xl' | '2xl';
  tag?: ElementType;
  color?: 'white' | 'muted-foreground' | 'muted' | 'black' | 'primary' | 'foreground' | 'neutral';
  onClick?: () => void;
}

export const Paragraph = forwardRef<HTMLDivElement, ParagraphProps>(
  (
    { children, innerHtml, className, size = 'lg', color = 'black', tag: Tag = 'div', onClick },
    ref,
  ) => {
    if (innerHtml) {
      return (
        <Tag
          ref={ref}
          onClick={onClick}
          className={cn(
            'tracking-tight transition-all',
            'ea-paragraph__inner-html',
            `ea-paragraph__inner-${size}`,
            `ea-paragraph__inner-${color}`,
            onClick && 'cursor-pointer text-opacity-80',
            className,
          )}
          dangerouslySetInnerHTML={{ __html: normalizeListSpacers(innerHtml) }}
        />
      );
    }

    return (
      <Tag
        ref={ref}
        onClick={onClick}
        className={cn(
          'ea-paragraph',
          'tracking-tight transition-all',
          size === 'xs' && 'text-[10px] leading-[130%] sm:text-[12px]',
          size === 'sm' && 'text-xs sm:text-sm',
          size === 'base' && 'text-sm sm:text-base',
          size === 'lg' && 'text-sm leading-[140%] sm:text-base sm:leading-[140%]',
          size === 'xl' && 'text-lg lg:text-xl',
          size === '2xl' && 'text-lg sm:text-xl lg:text-2xl',
          color === 'white' && 'text-white',
          color === 'muted-foreground' && 'text-[#666666]',
          color === 'neutral' && 'text-[#6B7280]',
          color === 'black' && 'text-black',
          color === 'muted' && 'text-muted',
          color === 'foreground' && 'text-foreground',
          color === 'primary' && 'text-primary',
          onClick && 'cursor-pointer text-opacity-80',
          className,
        )}
      >
        {children}
      </Tag>
    );
  },
);

Paragraph.displayName = 'Paragraph';
